import React, { useState } from "react";
import { Modal, Form, Input } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { startSignupHandoff, startGoogleSignupHandoff, startLoginHandoff } from "../../utils/signupHandoff";
import "./SignupPopup.scss";

// Sits above .lv2-input-bar (z-index 5000)
const POPUP_Z_INDEX = 5100;

// Same rules the portal enforces in Signup.tsx
const PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

const GoogleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
);

const SignupPopup = ({ visible, onCancel, redirectURL }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const onFinish = (values) => {
        const email = (values.email || "").trim().toLowerCase();
        const password = values.password || "";
        setSubmitting(true);
        startSignupHandoff({ email, password, redirectURL });
    };

    const handleGoogle = () => {
        if (submitting) return;
        setSubmitting(true);
        startGoogleSignupHandoff({ redirectURL });
    };

    const handleLogin = () => {
        if (submitting) return;
        setSubmitting(true);
        startLoginHandoff({ redirectURL });
    };

    const handleCancel = () => {
        if (submitting) return;
        form.resetFields();
        onCancel && onCancel();
    };

    return (
        <Modal
            className="signup-popup"
            width={440}
            zIndex={POPUP_Z_INDEX}
            destroyOnClose
            visible={visible}
            maskClosable={!submitting}
            closable={!submitting}
            footer={null}
            onCancel={handleCancel}
            title={null}
        >
            <h2 className="manrope f-24 w-700 lh-32 white signup-popup-title">
                Sign up to try ALL3D for free
            </h2>
            <p className="manrope f-14 w-400 lh-22 signup-popup-subtitle">
                Enter your email and a password to get started.
            </p>

            <Form
                form={form}
                layout="vertical"
                name="signup-popup-form"
                onFinish={onFinish}
                requiredMark={false}
            >
                <Form.Item
                    name="email"
                    rules={[
                        { type: "email", message: "Please enter a valid email" },
                        { required: true, message: "Please enter your email" },
                        { pattern: /^[^\s]+(\s+[^\s]+)*$/, message: "Spaces are not allowed" },
                    ]}
                >
                    <Input
                        size="large"
                        placeholder="Email address"
                        className="manrope f-14 w-400 signup-popup-input"
                        autoFocus
                        disabled={submitting}
                    />
                </Form.Item>

                <Form.Item
                    name="password"
                    rules={[
                        { required: true, message: "Please enter a password" },
                        {
                            pattern: PASSWORD_REGEX,
                            message:
                                "8+ chars with uppercase, lowercase, digit, and special character",
                        },
                    ]}
                >
                    <Input.Password
                        size="large"
                        placeholder="Password"
                        className="manrope f-14 w-400 signup-popup-input"
                        disabled={submitting}
                    />
                </Form.Item>

                <Form.Item>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn gradient-button manrope f-16 w-700 lh-22 white signup-popup-submit"
                    >
                        {submitting ? (
                            <>Continuing <LoadingOutlined /></>
                        ) : (
                            "Continue"
                        )}
                    </button>
                </Form.Item>
            </Form>

            <div className="signup-popup-divider">
                <hr />
                <span className="manrope f-14 w-400">or</span>
                <hr />
            </div>

            <button
                type="button"
                className="signup-popup-google manrope f-15 w-700"
                onClick={handleGoogle}
                disabled={submitting}
            >
                <GoogleIcon />
                <span>Continue with Google</span>
            </button>

            <div className="manrope f-14 w-400 lh-22 signup-popup-login">
                Already have an ALL3D account?{" "}
                <button
                    type="button"
                    className="signup-popup-login-link manrope f-14 w-600"
                    onClick={handleLogin}
                    disabled={submitting}
                >
                    Log in
                </button>
            </div>

            <p className="manrope f-12 w-400 lh-20 signup-popup-terms">
                By continuing, you accept ALL3D's{" "}
                <a href="/terms" target="_blank" rel="noreferrer">
                    Terms of Use
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                </a>
                .
            </p>
        </Modal>
    );
};

export default SignupPopup;
